import { supabase } from '../services/supabaseClient.ts';

/**
 * Exports user data as a JSON blob (GDPR compliant)
 * @returns Promise<void> - Downloads the data file
 */
export async function exportUserData(): Promise<void> {
  console.log('[accountService.exportUserData] Starting data export...');
  
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session?.access_token) {
      throw new Error('No valid session token');
    }

    console.log('[accountService.exportUserData] Retrieved access token from current session');
    console.log('[accountService.exportUserData] Token length:', session.access_token.length);
    console.log('[accountService.exportUserData] Token prefix:', session.access_token.substring(0, 20) + '...');

    // Use official Supabase Edge Function call
    const { data, error: fnError } = await supabase.functions.invoke('export-user-data', {
      headers: { Authorization: `Bearer ${session.access_token}` },
      body: {},
    });
    if (fnError) {
      console.error('[accountService.exportUserData] Supabase function error:', fnError);
      throw new Error(`Failed to export data: ${fnError.message || fnError}`);
    }
    if (!data?.success || !data?.data) {
      throw new Error('Invalid response from export service');
    }
    // Create and download the JSON file
    const dataBlob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
    if (typeof window !== 'undefined' && typeof window.document !== 'undefined') {
      const url = URL.createObjectURL(dataBlob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `safevibe-data-export-${new Date().toISOString().split('T')[0]}.json`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    console.log('[accountService.exportUserData] Data download initiated');
  } catch (err) {
    console.error('[accountService.exportUserData] Error:', err);
    throw err;
  }
}

/**
 * Permanently deletes user account and all associated data (GDPR compliant)
 * @returns Promise<void>
 */
export async function deleteUserAccount(): Promise<void> {
  console.log('[accountService.deleteUserAccount] Starting account deletion...');
  
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session?.access_token) {
      throw new Error('No valid session token');
    }

    console.log('[accountService.deleteUserAccount] Retrieved access token from current session');
    console.log('[accountService.deleteUserAccount] Token length:', session.access_token.length);
    console.log('[accountService.deleteUserAccount] Token prefix:', session.access_token.substring(0, 20) + '...');

    // Use official Supabase Edge Function call
    const { data, error: fnError } = await supabase.functions.invoke('delete-user-account', {
      headers: { Authorization: `Bearer ${session.access_token}` },
      body: {},
    });
    if (fnError) {
      console.error('[accountService.deleteUserAccount] Supabase function error:', fnError);
      throw new Error(`Failed to delete account: ${fnError.message || fnError}`);
    }
    if (!data?.success) {
      throw new Error('Invalid response from delete service');
    }
    // Sign out the user after successful deletion
    await supabase.auth.signOut();
    console.log('[accountService.deleteUserAccount] User signed out after deletion');
  } catch (err) {
    console.error('[accountService.deleteUserAccount] Error:', err);
    throw err;
  }
}

/**
 * Validates if user can perform account actions
 * @returns Promise<boolean>
 */
export async function validateUserAction(): Promise<boolean> {
  console.log('[accountService.validateUserAction] Validating user action...');
  
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session?.user) {
      console.log('[accountService.validateUserAction] No valid session');
      return false;
    }

    // Additional validation could include checking for recent login, etc.
    console.log('[accountService.validateUserAction] User action validated for:', session.user.id);
    return true;
  } catch (err) {
    console.error('[accountService.validateUserAction] Error:', err);
    return false;
  }
} 